import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@predoc/cow';
import { useGetPatients } from '@/feature/patients/hooks/patientsAPI';
import { DataDeletionRequestLevel, DataDeletionFormState } from '../types';
import { SelectionStep } from './SelectionStep';
import { ConfirmationStep } from './ConfirmationStep';

interface RequestDataDeletionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialFormState: DataDeletionFormState = {
  deletionType: DataDeletionRequestLevel.SPECIFIC_PATIENTS,
  deletionDate: undefined,
  selectedPatients: [],
  selectedProfiles: [],
  isExportData: false,
};

export const RequestDataDeletionModal = ({
  open,
  onOpenChange,
}: RequestDataDeletionModalProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [formState, setFormState] =
    useState<DataDeletionFormState>(initialFormState);

  const { data: patientsData } = useGetPatients(
    {
      search: { terms: [] },
      pagination: { offset: 0, limit: 1 },
      includeRequests: false,
    },
    { enabled: open }, // only fetch data if modal is open
  );

  const totalPatientCount = patientsData?.totalCount ?? 0;

  const handleClose = () => {
    onOpenChange(false);
    setStep(1);
    setFormState(initialFormState);
  };

  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSuccess = () => {
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col p-0 sm:max-w-[800px]">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Request Data Deletion</DialogTitle>
          <DialogDescription className="sr-only">
            Select patients or profiles to request data deletion
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <SelectionStep
            formState={formState}
            setFormState={setFormState}
            onCancel={handleClose}
            onNext={handleNext}
            totalPatientCount={totalPatientCount}
          />
        ) : (
          <ConfirmationStep
            formState={{
              ...formState,
              deletionDate: formState.deletionDate as Date,
            }}
            setFormState={setFormState}
            onCancel={handleClose}
            onBack={handleBack}
            onSuccess={handleSuccess}
            totalPatientCount={totalPatientCount}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
